import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Search, Trophy, Swords, ShieldAlert, ArrowRight, Zap } from "lucide-react";
import PublicRacerModal from "@/components/PublicRacerModal";

export default function Stats() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ racers: [], syndicates: [] });
  const [searching, setSearching] = useState(false);
  const [activeRacerId, setActiveRacerId] = useState(null);

  useEffect(() => {
    if (!query.trim()) {
      setResults({ racers: [], syndicates: [] });
      return;
    }
    setSearching(true);
    const delayDebounce = setTimeout(() => {
      api.get(`/stats/search?q=${encodeURIComponent(query)}`)
        .then((res) => {
          setResults(res.data);
        })
        .catch((err) => console.error("Search error:", err))
        .finally(() => setSearching(false));
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [query]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
      <div className="small-label text-rtrl-red">Archives &amp; Records</div>
      <h1 className="font-display uppercase text-5xl md:text-6xl text-white mt-2">HoloNet Stats Index</h1>
      <p className="text-white/60 mt-2 max-w-2xl">Search and retrieve career performance profiles, points totals, historical matchup statistics, and driver rosters across all current and archived seasons.</p>

      {/* Search Input HUD */}
      <div className="mt-10 relative max-w-2xl rtrl-card carbon-bg p-2 flex items-center border border-surface-line shadow-[0_0_30px_rgba(229,25,25,0.05)]">
        <Search className="text-white/30 ml-3 shrink-0" />
        <input
          type="text"
          className="bg-transparent border-none outline-none font-mono-tech text-white text-lg px-3 py-2 w-full placeholder-white/20"
          placeholder="Search pilot name or syndicate..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="stats-search-input"
        />
        {searching && (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-rtrl-red mr-3 shrink-0"></div>
        )}
      </div>

      <div className="mt-12 grid md:grid-cols-2 gap-8">
        {/* Racers Result column */}
        <div className="space-y-4">
          <div className="small-label text-rtrl-silver border-b border-surface-line pb-2 flex items-center gap-2">
            <Swords className="w-4 h-4 text-rtrl-red" /> Matching Pilots
          </div>
          {results.racers.map((r) => (
            <div
              key={r.id}
              onClick={() => setActiveRacerId(r.id)}
              className="rtrl-card carbon-bg p-4 flex items-center justify-between hover:border-rtrl-red/60 cursor-pointer group transition-all"
              data-testid={`stats-racer-card-${r.id}`}
            >
              <div>
                <div className="text-white font-body font-bold text-lg group-hover:text-rtrl-red transition-colors flex items-center gap-2">
                  {r.display_name} {r.number !== null && <span className="text-rtrl-red font-mono-tech text-sm font-bold">#{r.number}</span>}
                </div>
                <div className="small-label mt-1 text-white/40">{r.platform || "PC"} · {r.region || "NA"}</div>
              </div>
              <ArrowRight className="text-white/20 group-hover:text-rtrl-red group-hover:translate-x-1 transition-all w-5 h-5 shrink-0" />
            </div>
          ))}
          {query.trim() && results.racers.length === 0 && !searching && (
            <div className="text-white/30 text-sm italic py-4">No matching racers found.</div>
          )}
        </div>

        {/* Syndicates Result column */}
        <div className="space-y-4">
          <div className="small-label text-rtrl-silver border-b border-surface-line pb-2 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-rtrl-red" /> Matching Syndicates
          </div>
          {results.syndicates.map((s) => (
            <Link
              key={s.id}
              to={`/syndicates/${s.id}`}
              className="rtrl-card carbon-bg p-4 flex items-center justify-between hover:border-rtrl-red/60 cursor-pointer group transition-all block"
              data-testid={`stats-syn-card-${s.id}`}
            >
              <div>
                <div className="text-white font-body font-bold text-lg group-hover:text-rtrl-red transition-colors flex items-center gap-3">
                  {s.logo_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${s.logo_url}`} alt="" className="w-6 h-6 rounded-full object-contain bg-void/50 shrink-0" />
                  ) : (
                    <span className="w-2.5 h-2.5 inline-block shrink-0" style={{ background: s.color }} />
                  )}
                  {s.name}
                </div>
                <div className="small-label mt-1 text-white/40">{s.tag} · {s.bio ? "Registered Syndicate" : "HoloNet profile"}</div>
              </div>
              <ArrowRight className="text-white/20 group-hover:text-rtrl-red group-hover:translate-x-1 transition-all w-5 h-5 shrink-0" />
            </Link>
          ))}
          {query.trim() && results.syndicates.length === 0 && !searching && (
            <div className="text-white/30 text-sm italic py-4">No matching syndicates found.</div>
          )}
        </div>
      </div>

      {!query.trim() && (
        <div className="mt-16 rtrl-card carbon-bg p-8 text-center max-w-xl mx-auto border border-surface-line">
          <Zap className="w-10 h-10 text-rtrl-red mx-auto mb-4 animate-pulse" />
          <h3 className="font-display uppercase text-2xl text-white">Central Stats Uplink</h3>
          <p className="text-white/60 text-sm mt-2">Enter a pilot's callsign or syndicate's team name into the console above to query live standings, placement records, and championship performance history across seasons.</p>
        </div>
      )}

      <PublicRacerModal racerId={activeRacerId} onClose={() => setActiveRacerId(null)} />
    </div>
  );
}